import { useCallback, useState } from "react";
import { workflowEngineService } from "@/services/workflowEngine/workflowEngineService";
import type { Workflow } from "@/types/aiToolkit";
import type { WorkflowRun } from "@/types/workflowEngine";

export function useWorkflowRun() {
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [progressLabel, setProgressLabel] = useState<string | null>(null);

  const startWorkflow = useCallback(
    async (
      workflow: Workflow,
      triggerPayload?: Record<string, unknown>,
      options?: { triggerType?: string; versionId?: string | null }
    ): Promise<WorkflowRun | null> => {
      setIsRunning(true);
      setProgressLabel("Starting…");
      try {
        const run = await workflowEngineService.startRun(
          {
            workflow,
            versionId: options?.versionId,
            triggerType: options?.triggerType ?? "manual",
            triggerPayload,
          },
          {
            onNodeStart: (nodeId, nodeType) => setProgressLabel(`${nodeType}: ${nodeId}`),
            onNodeComplete: () => setProgressLabel(null),
            onPaused: (reason) => setProgressLabel(`Paused: ${reason}`),
          }
        );
        if (run) setActiveRunId(run.id);
        return run;
      } finally {
        setIsRunning(false);
        setProgressLabel(null);
      }
    },
    []
  );

  const publishWorkflow = useCallback(async (workflow: Workflow, changelog?: string) => {
    return workflowEngineService.publishVersion(workflow, changelog);
  }, []);

  return {
    activeRunId,
    setActiveRunId,
    isRunning,
    progressLabel,
    startWorkflow,
    publishWorkflow,
  };
}
